<template>
  <div class="contact-reveal">
    <button
      v-if="!value && !loading"
      type="button"
      class="contact-reveal__button link"
      :aria-disabled="loading"
      :aria-label="`${label}: ${contactName}`"
      :aria-controls="resultId"
      @click="reveal"
    >
     {{ label }}
    </button>
    <span :id="resultId" 
          class="contact-reveal__result" 
          aria-live="polite" 
          aria-atomic="true" 
          :aria-busy="loading"
    >
      <a v-if="value" :href="href">{{ value }}</a>
      <span v-else-if="loading">{{ translations.revealingContact }}</span>
      <span v-else-if="errorMessage" class="error-component">{{ errorMessage }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useFetch } from '@utils/fetch-service';
  import { HttpError } from '@utils/models/HttpError';
  import type { IFindSrfConsultantPageTranslations } from '../../interfaces/IFindSrfConsultantPageTranslations';

  const props = defineProps<{
    contactId: string;
    contactName: string;
    contactType: 'email' | 'phone';
    translations: IFindSrfConsultantPageTranslations;
  }>();

  const { post, loading } = useFetch('/findsrfconsultantapp/reveal');
  const value = ref('');
  const errorMessage = ref('');
  const label = computed(() => props.contactType === 'email' ? props.translations.showEmail : props.translations.showPhone);
  const resultId = computed(() => `contact-reveal-${props.contactId}-${props.contactType}`);
  const href = computed(() => props.contactType === 'email'
    ? `mailto:${encodeURIComponent(value.value).replace(/%40/g, '@')}`
    : `tel:${value.value.replace(/[^+\d]/g, '')}`);

  async function reveal() {
    if (loading.value || value.value) {
      return;
    }

    errorMessage.value = '';
    try {
      const response = await post<{ value: string }>({
        contactId: props.contactId,
        contactType: props.contactType,
      }, { cache: 'no-store' });
      if (typeof response.value !== 'string' || !response.value.trim()) {
        throw new Error('Missing contact value');
      }

      value.value = response.value;
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        errorMessage.value = props.translations.contactUnavailable;
      } else {
        errorMessage.value = props.translations.revealError;
      }
    }
  }
</script>
